import React, { useState } from 'react';
import { Link } from 'react-router';
import { LEGAL_DOCUMENTS, upcomingLegalVersion } from '@lds-efsp/shared-contracts';
import type { LegalDocumentId } from '@lds-efsp/shared-contracts';
import { MarkdownContent } from '../components/MarkdownContent';

interface LegalUpcomingDocumentPageProps {
  /**
   * Resolved by the route declaration in App.tsx — one public route per
   * registry document id, no URL-param parsing in between.
   */
  documentId: LegalDocumentId;
}

/**
 * The one shared page for the public /legal/<id>/upcoming routes: the text a
 * document has ANNOUNCED but does not yet govern. It is the in-force page's
 * sibling rather than a mode of it — the two ask the registry different
 * questions (what governs at this instant, what is announced at it) and render
 * different chrome for the answers, so neither is a flag on the other.
 *
 * Readable signed in or out — no auth guard. The registry reads no clock, so
 * the page pins the visit instant once per mount and hands it to the selector;
 * which branch renders is that selector's answer alone. Nothing here compares a
 * version's own dates against a clock: a version stops being upcoming because
 * `upcomingLegalVersion` stops answering it, not because this page noticed the
 * date. That is what flips the address on the effective instant with no deploy.
 */
const LegalUpcomingDocumentPage: React.FC<LegalUpcomingDocumentPageProps> = ({ documentId }) => {
  const document = LEGAL_DOCUMENTS.find((entry) => entry.id === documentId);
  // Pin the visit instant once per mount, exactly as the in-force page does:
  // one visit should show one answer, and the state initializer is the
  // render-sanctioned place to read the clock.
  const [now] = useState(() => new Date().toISOString());
  const version = upcomingLegalVersion(documentId, now);

  if (document === undefined) {
    // Typed-impossible at the wired call sites (the route table names a
    // registry id); renderable rather than a crash the day one is renamed.
    return (
      <div className="mx-auto max-w-3xl px-4 py-8">
        <p className="text-gray-500">This document is not yet available.</p>
      </div>
    );
  }

  if (version === undefined) {
    // No announced text: the ordinary state for a document that never has a
    // notice gap, and the state every document returns to once its announced
    // version takes effect. No version body is rendered — there is none to
    // render — so a bookmark of this address shows the message and the way
    // back to whatever governs.
    return (
      <div className="mx-auto max-w-3xl px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-900">{document.title}</h1>
        <p className="mt-4 text-gray-700">
          There is no upcoming version of this document.
        </p>
        <p className="mt-4 text-sm">
          <Link to={`/legal/${documentId}`} className="font-medium underline">
            View the current version
          </Link>
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900">{document.title}</h1>
      {/* The ISO date part is the UTC date — identical on every machine, no Intl. */}
      <p className="mt-1 text-sm text-gray-500">
        Takes effect {version.effectiveDate.slice(0, 10)} · NOT YET IN FORCE
      </p>
      <div className="mt-6">
        <MarkdownContent markdown={version.body} />
      </div>
      <p className="mt-6 text-sm">
        <Link to={`/legal/${documentId}`} className="font-medium underline">
          View the current version
        </Link>
      </p>
    </div>
  );
};

export default LegalUpcomingDocumentPage;
